import type { StackProps } from '@chakra-ui/react'
import { Icon, IconButton } from '@chakra-ui/react'
import type { FC } from 'react'
import { memo } from 'react'
import { PiLockKeyFill } from 'react-icons/pi'

import { Card, CardTitle } from '../components'

import { Tooltip } from '@app/components/ui/tooltip'
import useIsAnonymousUser from '@app/hooks/useIsAnonymousUser'
import useStoreCurrentUser from '@app/hooks/useStoreCurrentUser'
import DomainObjectStatusPopover from '@app/next/domainObjectStatusPopover'
import CollapsedNodes from '@app/pages/strategies/components/nodes/components/collapsedNodes'
import Can from '@app/shared/authorization/can'
import { abilitiesFor } from '@app/shared/authorization/caslProvider'
import cardFontSize from '@app/shared/cards/cardFontSize'
import cardMetadata from '@app/shared/cards/cardMetadata'
import CardLabels from '@app/shared/cards/components/cardLabels'
import CardSection from '@app/shared/cards/components/cardSection'
import CardSharedActions from '@app/shared/cards/components/cardSharedActions'
import CardTypeSection from '@app/shared/cards/components/cardTypeSection'
import CardStrategyCTA from '@app/shared/cards/subMap/cardStrategyCTA'
import MarkdownDisplay from '@app/shared/markdownDisplay'
import type { DomainNode, DomainStrategy } from '@app/types'
import ICON_MAP from '@app/utils/iconMap'
import { SharingTypesEnum } from '@graphql/types'

interface Props extends StackProps {
  strategyId: string
  strategy?: DomainStrategy
  node?: DomainNode
  mapMode?: string
}

const SubMapCard: FC<Props> = ({ strategyId, strategy, node, mapMode = 'build', ...rest }) => {
  const { user } = useStoreCurrentUser()
  const isAnonymousUser = useIsAnonymousUser(strategyId)
  const abilities = abilitiesFor(user)
  const canViewAdmin = abilities.can('view', 'admin')
  const { size, color } = cardMetadata(node)
  const { id, sharing, description, empty } = strategy

  const fontSize = cardFontSize('sm', size)
  const route = isAnonymousUser ? null : `/strategy/${strategyId}/map/strategy/${id}`
  const showCTA = canViewAdmin && !isAnonymousUser && mapMode === 'build' && empty

  const lockedDisplay = sharing !== SharingTypesEnum.PublicAccess && (
    <Tooltip content="This card will not display to logged out users unless you make it public." showArrow>
      <IconButton cursor="default" aria-label="Sharing" variant="ghost">
        <Icon boxSize={size === 'normal' ? 5 : 6} color="fg.subtle">
          <PiLockKeyFill />
        </Icon>
      </IconButton>
    </Tooltip>
  )

  return (
    <Card color={color} {...rest}>
      <CardTypeSection title="Map" icon={ICON_MAP.StrategyMap} previewId={id} route={route} size={size} />
      <CardSection size={size}>
        <CardTitle domainObject={strategy} name="name" size={size} />
        {description && <MarkdownDisplay text={description} fontSize={fontSize} />}
      </CardSection>
      {showCTA && (
        <Can I="create" a="strategy">
          <CardStrategyCTA parentStrategyId={strategyId} id={id} size={size} />
        </Can>
      )}
      <CardLabels fieldName="labels" domainObject={strategy} size={size} />
      <CardSharedActions domainObject={strategy} size={size}>
        <DomainObjectStatusPopover domainObject={strategy} mx={2} />
        {lockedDisplay}
      </CardSharedActions>
      {node && <CollapsedNodes node={node} />}
    </Card>
  )
}

export default memo(SubMapCard)
